import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Upload, CheckCircle, AlertTriangle, Play,
  FileText, BarChart3, Loader2, Copy, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const SAMPLE_MANIFEST = `version: 1
org_id: org_example
agents:
  - name: sales-research
    framework: crewai
    owner: jane@company.com
    department: Sales
    description: Researches prospects using CRM data
    tools: [web_search, crm_read]
  - name: code-reviewer
    framework: langgraph
    owner: bob@company.com
    department: Engineering
    description: Reviews PRs for security issues
    tools: [github_read, static_analysis]
schedules:
  - role: hunter
    cron: "0 */6 * * *"
routes:
  - severity: high
    channel: slack
    target: https://hooks.slack.com/services/T00/B00/xxx
  - severity: critical
    channel: email
    target: secops@company.com`;

export default function StackDeployPage() {
  const [yaml, setYaml] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState('');

  const loadSample = () => {
    setYaml(SAMPLE_MANIFEST);
    setValidationResult(null);
    setAnalysisResult(null);
    setApplyResult(null);
  };

  const validate = async () => {
    if (!yaml.trim()) return;
    setLoading('validate');
    try {
      const data = await api.validateStackManifest(yaml);
      setValidationResult(data);
      if (data.valid) {
        toast.success('Manifest is valid');
      } else {
        toast.error('Validation failed');
      }
    } catch (err) {
      toast.error('Validation failed');
    }
    setLoading('');
  };

  const analyze = async () => {
    if (!yaml.trim()) return;
    setLoading('analyze');
    try {
      const data = await api.analyzeStackManifest(yaml);
      setAnalysisResult(data);
      toast.success(`Analysis complete — ${data.overall_risk_level} risk`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    }
    setLoading('');
  };

  const apply = async () => {
    if (!yaml.trim()) return;
    setLoading('apply');
    try {
      const data = await api.applyStackManifest(yaml);
      setApplyResult(data);
      toast.success(`Manifest applied: ${data.agents_created} agents, ${data.schedules_created} schedules`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    }
    setLoading('');
  };

  const riskColor = (level) => {
    if (level === 'critical') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (level === 'high') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    if (level === 'medium') return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
    return 'text-green-400 bg-green-500/10 border-green-500/20';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/agents" className="text-slate-400 hover:text-white transition-colors text-sm">Agent Control Plane</Link>
            <Link to="/agents/detection" className="text-slate-400 hover:text-white transition-colors text-sm">Detection</Link>
            <span className="text-white font-medium text-sm">Stack Deploy</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
              <Upload className="w-6 h-6 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Stack Deploy</h1>
          </div>
          <p className="text-slate-400 text-sm">
            Deploy agent fleets, schedules, and alert routing from a single YAML manifest.
            Config-as-code for your AI agent infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm text-slate-400">YAML Manifest</label>
              <Button variant="ghost" size="sm" onClick={loadSample} className="text-slate-400 hover:text-white">
                <FileText className="w-3 h-3 mr-1" />Load Sample
              </Button>
            </div>
            <Textarea
              value={yaml}
              onChange={e => setYaml(e.target.value)}
              placeholder="Paste your stack-deploy YAML manifest here..."
              className="bg-slate-800/50 border-slate-700 text-slate-200 font-mono text-sm min-h-[450px]"
            />
            <div className="flex items-center gap-3 mt-4">
              <Button onClick={validate} disabled={loading === 'validate' || !yaml.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {loading === 'validate' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Validate
              </Button>
              <Button onClick={analyze} disabled={loading === 'analyze' || !yaml.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white">
                {loading === 'analyze' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Analyze Risk
              </Button>
              <Button onClick={apply} disabled={loading === 'apply' || !yaml.trim()}
                className="bg-green-600 hover:bg-green-700 text-white">
                {loading === 'apply' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Apply
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {/* Validation result */}
            {validationResult && (
              <div className={`rounded-xl p-4 border ${validationResult.valid ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {validationResult.valid
                    ? <CheckCircle className="w-5 h-5 text-green-400" />
                    : <AlertTriangle className="w-5 h-5 text-red-400" />}
                  <span className={`font-medium ${validationResult.valid ? 'text-green-400' : 'text-red-400'}`}>
                    {validationResult.valid ? 'Valid Manifest' : 'Invalid Manifest'}
                  </span>
                </div>
                {validationResult.warnings?.length > 0 && (
                  <div className="space-y-1">
                    {validationResult.warnings.map((w, i) => (
                      <div key={i} className="text-xs text-yellow-400 font-mono flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}
                      </div>
                    ))}
                  </div>
                )}
                {validationResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    {validationResult.errors.map((e, i) => (
                      <div key={i} className="text-xs text-red-400 font-mono">{e}</div>
                    ))}
                  </div>
                )}
                {validationResult.manifest && (
                  <div className="mt-3 text-xs text-slate-400">
                    {validationResult.manifest.agents?.length || 0} agents,{' '}
                    {validationResult.manifest.schedules?.length || 0} schedules,{' '}
                    {validationResult.manifest.routes?.length || 0} routes
                  </div>
                )}
              </div>
            )}

            {/* Analysis result */}
            {analysisResult && (
              <div className="rounded-xl p-4 border border-slate-700/50 bg-slate-800/50">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-white">Risk Analysis</span>
                  <span className={`px-2 py-0.5 rounded text-xs border ${riskColor(analysisResult.overall_risk_level)}`}>
                    {analysisResult.overall_risk_level} risk
                  </span>
                  <span className="text-slate-500 text-xs ml-auto">Score: {analysisResult.average_risk_score}</span>
                </div>
                <div className="space-y-3">
                  {analysisResult.agent_reports?.map((r, i) => (
                    <div key={i} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white text-sm font-medium">{r.name}</span>
                        <span className="text-slate-500 text-xs">{r.framework}</span>
                        <span className={`px-2 py-0.5 rounded text-xs border ml-auto ${riskColor(r.risk_level)}`}>
                          {r.risk_score}/100
                        </span>
                      </div>
                      {r.risks.length > 0 && (
                        <div className="space-y-1">
                          {r.risks.map((risk, j) => (
                            <div key={j} className="text-xs text-slate-400 flex items-start gap-1">
                              <AlertTriangle className={`w-3 h-3 mt-0.5 shrink-0 ${risk.severity === 'critical' ? 'text-red-400' : risk.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'}`} />
                              {risk.detail}
                            </div>
                          ))}
                        </div>
                      )}
                      {r.risks.length === 0 && <div className="text-xs text-green-400">No risks detected</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Apply result */}
            {applyResult && (
              <div className="rounded-xl p-4 border border-green-500/20 bg-green-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-green-400">Manifest Applied</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">{applyResult.agents_created}</div>
                    <div className="text-xs text-slate-400">Agents</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{applyResult.schedules_created}</div>
                    <div className="text-xs text-slate-400">Schedules</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">{applyResult.routes_created}</div>
                    <div className="text-xs text-slate-400">Routes</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-mono">{applyResult.manifest_id}</span>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(applyResult.manifest_id); toast.success('Copied!'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!validationResult && !analysisResult && !applyResult && (
              <div className="text-center py-16 text-slate-500">
                <Upload className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="mb-2">Paste a YAML manifest and validate, analyze, or apply it.</p>
                <Button variant="ghost" size="sm" onClick={loadSample} className="text-slate-400 hover:text-white">
                  Load sample manifest
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
